import React, { useEffect, useState, useMemo, useCallback } from "react";

// Modules
import * as editorHTML from "editorjs-html";

// App
import { filesystem_root, subdomain, proxy } from "../../../../../config";
import {
  processObject,
  isSafari,
  jsonParser,
  getThemeJson,
} from "../../../../../utils/helpers";
import { default_reveal } from "../../../../../core/defaultReveal";
import ImageGallery from "../../imageGallery";
import FullscreenImageGallery from "../../fullscreenImageGallery";
import BespokeImageGallery from "../../bespokeImageGallery";
import AudioPlayer from "../../../../../partials/audioPlayer";
import { draggable } from "../../../../../core/editorOptions";
import { backgroundImageURIGenerator } from "../../../../../core/backgroundImageURIGenerator";

// UI components
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { IconButton } from "@mui/material";
import { AccordionArrow } from "../../../../../editorjs/icons/icons";

// Block parsers
import {
  titleParser,
  subtitleParser,
  blockHeadingParser,
  listParser,
  tableParser,
  quoteParser,
  bodyParser,
  ruleParser,
  imageParser,
  inlineImageParser,
  ancillaryParser,
  iconParser,
  videoParser,
  audioParser,
  contactParser,
  embedParser,
  clientParser,
  pointParser,
  pillarParser,
  layoutParser,
  locationHeaderParser,
  hotelCapacityParser,
  locationFooterParser,
  hotelTextOverlayParser,
  popupParser,
  buttonGroupParser,
  imageTextOverlayParser,
  captionedImageParser,
  shapeParser,
  mapParser,
} from "./blockParsers.js";

const Iframe = React.memo((props) => {
  const src = useMemo(() => {
    const isLibrary =
      parseInt(props.slide.initial_presentation) !==
      parseInt(props.presentation_id);

    if (isLibrary) {
      return props.slide.field_migration_id
        ? `${proxy}/${subdomain}/presentations/${props.slide.initial_presentation}/${props.slide.initial_presentation}/${props.id}.html`
        : `${proxy}/${subdomain}/presentations/${props.slide.initial_presentation}/${props.slide.initial_presentation}/${props.id}/${props.id}.html`;
    }

    if (props.live) {
      return props.slide.field_migration_id
        ? `${proxy}/${subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${props.id}.html`
        : `${proxy}/${subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/${props.id}/${props.id}.html`;
    }

    return `${proxy}${props.slide.slide_html_path}`;
  }, [
    props.slide.initial_presentation,
    props.presentation_id,
    props.id,
    props.live,
    props.slide.field_migration_id,
    props.slide.slide_html_path,
  ]);

  useEffect(() => {
    props.onTest(src);
  }, [src, props.onTest]);

  return (
    <iframe
      id={`frame-${props.id}`}
      className="advanced-slide"
      title={`Advanced Slide ${props.id}`}
      data-src={src}
    />
  );
});

const CountryMapIframe = React.memo((props) => {
  const {
    theme_json,
    themeDirectory,
    presentation_id,
    json,
    live,
    slide,
    blockData: propBlockData,
  } = props;

  const { src, mapData } = useMemo(() => {
    const blockData =
      propBlockData || json.blocks.find((e) => e.type === "countryMap")?.data;

    const currentUrl = window.location.href;
    const urlParams = new URLSearchParams(currentUrl.split("?")[1]);
    const hash = urlParams.get("hash") || null;

    const mapData = blockData ? blockData.mapData : undefined;
    let src;

    const apiKey = theme_json?.googleApiKey;
    if (apiKey) {
      const pinSrc = `${themeDirectory}/dist/images/icons/${theme_json?.icons?.pin}`;

      let params = `key=${apiKey}&pin=${pinSrc}&presentation=${presentation_id}&viewMode=${
        live ? "view" : "preview"
      }&sub=${subdomain}`;

      if (hash) {
        params += `&hash=${hash}`;
      }

      src = `/country-map.html?${params}`;
    }

    return { src, mapData };
  }, [
    theme_json,
    themeDirectory,
    presentation_id,
    json.blocks,
    live,
    propBlockData,
  ]);

  if (!src) {
    return (
      <div className="d-flex h-100 justify-content-center align-items-center">
        <b>Map can't be displayed - API key error</b>
      </div>
    );
  }

  return (
    <iframe
      title="map-slide"
      id={`country-map-frame-${slide.id}`}
      src={src}
      onLoad={() => {
        document
          .querySelector(`#country-map-frame-${slide.id}`)
          ?.contentWindow.postMessage({ mapData }, "*");
      }}
    />
  );
});

const CustomLayoutCountryMap = React.memo((props) => {
  const countryMapBlock = props.json.blocks.find(
    (e) => e.type === "countryMap"
  );
  if (countryMapBlock) {
    const blockData = countryMapBlock?.data;
    return (
      <div
        className="map-wrapper"
        style={parseStyleString(draggable(countryMapBlock))}
      >
        <div className="map">
          <div className="map-container">
            <CountryMapIframe
              blockData={blockData}
              theme_json={props.theme_json}
              themeDirectory={props.themeDirectory}
              presentation_id={props.presentation_id}
              live={props.live}
              json={props.json}
              slide={{ id: props.slide.id }}
            />
          </div>
        </div>
      </div>
    );
  } else {
    return "";
  }
});

const parseStyleString = (styleString) => {
  return styleString.split(";").reduce((styleObject, styleProperty) => {
    if (styleProperty) {
      const [property, value] = styleProperty
        .split(":")
        .map((item) => item.trim());
      const camelCaseProperty = property.replace(/-([a-z])/g, (match, letter) =>
        letter.toUpperCase()
      );
      styleObject[camelCaseProperty] = value.replace(/['"]/g, ""); // remove quotes
    }
    return styleObject;
  }, {});
};

function Slide(props) {
  const [contentCount, setContentCount] = useState(0);
  const [hasImage, setHasImage] = useState(false);
  const [hasSubtitle, setHasSubtitle] = useState(false);

  // slide contents
  const json = useMemo(
    () => jsonParser(props.slide.slide_json),
    [props.slide.slide_json]
  );

  const theme_json = useMemo(
    () => getThemeJson(props.slide.theme.label),
    [props.slide.theme.label]
  );

  const themeDirectory = useMemo(
    () =>
      props.live
        ? `${filesystem_root}/${subdomain}/presentations/${props.presentation_id}/${props.presentation_id}/assets/themes/${props.slide?.theme?.label}`
        : `${filesystem_root}/${subdomain}/themes/${props.slide?.theme?.label}`,
    [props.live, props.presentation_id, props.slide?.theme?.label]
  );

  const [isSlideEmpty, setSlideEmpty] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  // galleries
  const [images, setImages] = useState([]);
  const [title, setTitle] = useState("");

  // View presentation context

  // text-toggle layout
  const [toggleActive, setToggleActive] = useState(false);

  if (!json?.blocks) {
    json.blocks = [];
  }

  if (!json.reveal) {
    json.reveal = default_reveal;
  }

  json.blocks.map((obj) => processObject({ ...obj }));

  // editorParser
  const editorParser = useMemo(
    () =>
      editorHTML({
        title: (block) => titleParser(block),
        subtitle: (block) => subtitleParser(block),
        blockHeading: (block) => blockHeadingParser(block),
        list: (block) => listParser(block),
        table: (block) => tableParser(block, json, theme_json),
        quote: (block) => quoteParser(block),
        body: (block) => bodyParser(block, json),
        rule: (block) => ruleParser(block),
        image: (block) =>
          imageParser(block, props.live, props.presentation_id, json),
        inlineImage: (block) =>
          inlineImageParser(block, props.live, props.presentation_id, json),
        ancillary: (block) => ancillaryParser(block),
        icon: (block) => iconParser(block, json),
        video: (block) =>
          videoParser(block, props.live, props.presentation_id, json),
        audio: (block) => audioParser(block, props.live, props.presentation_id),
        contact: (block) =>
          contactParser(block, props.live, props.presentation_id),
        embed: (block) => embedParser(block),
        client: (block) =>
          clientParser(block, props.live, props.presentation_id),
        point: (block) =>
          pointParser(block, props.live, props.presentation_id, json),
        pillar: (block) =>
          pillarParser(block, props.live, props.presentation_id, json),
        layout: (block) => layoutParser(block, json),
        locationHeader: (block) => locationHeaderParser(block),
        hotelCapacity: (block) => hotelCapacityParser(block),
        locationFooter: (block) => locationFooterParser(block),
        hotelTextOverlay: (block) => hotelTextOverlayParser(block),
        popup: (block) => popupParser(block, props.slide),
        buttonGroup: (block) => buttonGroupParser(block),
        imageTextOverlay: (block) =>
          imageTextOverlayParser(
            block,
            props.live,
            props.presentation_id,
            json
          ),
        captionedImage: (block) =>
          captionedImageParser(block, props.live, props.presentation_id, json),
        shape: (block) => shapeParser(block),
        map: (block) =>
          json.reveal.slideLayout.id === "custom"
            ? mapParser(
                block,
                props.live,
                props.slide,
                json,
                theme_json,
                themeDirectory
              )
            : "",
        countryMap: () => {
          return "";
        },
        gallery: () => {
          return "";
        },
      }),
    [
      json,
      theme_json,
      props.live,
      props.presentation_id,
      props.slide,
      themeDirectory,
    ]
  );

  // mapParser
  const editorMapParser = useMemo(
    () =>
      editorHTML({
        title: () => {
          return "";
        },
        subtitle: () => {
          return "";
        },
        blockHeading: () => {
          return "";
        },
        list: () => {
          return "";
        },
        table: () => {
          return "";
        },
        quote: () => {
          return "";
        },
        body: () => {
          return "";
        },
        rule: () => {
          return "";
        },
        image: () => {
          return "";
        },
        inlineImage: () => {
          return "";
        },
        ancillary: () => {
          return "";
        },
        icon: () => {
          return "";
        },
        video: () => {
          return "";
        },
        audio: () => {
          return "";
        },
        contact: () => {
          return "";
        },
        embed: () => {
          return "";
        },
        client: () => {
          return "";
        },
        point: () => {
          return "";
        },
        pillar: () => {
          return "";
        },
        popup: () => {
          return "";
        },
        buttonGroup: () => {
          return "";
        },
        imageTextOverlay: () => {
          return "";
        },
        captionedImage: () => {
          return "";
        },
        map: (block) =>
          mapParser(
            block,
            props.live,
            props.slide,
            json,
            theme_json,
            themeDirectory
          ),
        gallery: () => {
          return "";
        },
      }),
    [json, props.live, props.slide, themeDirectory, theme_json]
  );

  if (!json.reveal.slideTransition)
    json.reveal.slideTransition = default_reveal.slideTransition;
  if (!json.reveal.slideLogo) json.reveal.slideLogo = default_reveal.slideLogo;
  if (!json.reveal.slideLogoPosition)
    json.reveal.slideLogoPosition = default_reveal.slideLogoPosition;
  if (!json.reveal.slideLayout)
    json.reveal.slideLayout = default_reveal.slideLayout;
  if (!json.reveal.slideLayoutScheme)
    json.reveal.slideLayoutScheme = default_reveal.slideLayoutScheme;
  if (!json.reveal.slideContentPosition)
    json.reveal.slideContentPosition = default_reveal.slideContentPosition;

  if (!json.reveal.slideBackgroundColor)
    json.reveal.slideBackgroundColor = default_reveal.slideBackgroundColor;
  if (!json.reveal.slideBackgroundTransition)
    json.reveal.slideBackgroundTransition =
      default_reveal.slideBackgroundTransition;
  if (!json.reveal.slideBackgroundImage)
    json.reveal.slideBackgroundImage = default_reveal.slideBackgroundImage;
  if (!json.reveal.slideBackgroundOpacity)
    json.reveal.slideBackgroundOpacity = default_reveal.slideBackgroundOpacity;
  if (!json.reveal.slideBackgroundRepeat)
    json.reveal.slideBackgroundRepeat = default_reveal.slideBackgroundRepeat;
  if (!json.reveal.slideBackgroundPosition)
    json.reveal.slideBackgroundPosition =
      default_reveal.slideBackgroundPosition;
  if (!json.reveal.slideBackgroundSize)
    json.reveal.slideBackgroundSize = default_reveal.slideBackgroundSize;

  if (!json.reveal.slideInnerBackgroundColor)
    json.reveal.slideInnerBackgroundColor =
      default_reveal.slideInnerBackgroundColor;
  if (!json.reveal.slideInnerBackgroundImage)
    json.reveal.slideInnerBackgroundImage =
      default_reveal.slideInnerBackgroundImage;
  if (!json.reveal.slideInnerBackgroundOpacity)
    json.reveal.slideInnerBackgroundOpacity =
      default_reveal.slideInnerBackgroundOpacity;
  if (!json.reveal.slideInnerBackgroundRepeat)
    json.reveal.slideInnerBackgroundRepeat =
      default_reveal.slideInnerBackgroundRepeat;
  if (!json.reveal.slideInnerBackgroundPosition)
    json.reveal.slideInnerBackgroundPosition =
      default_reveal.slideInnerBackgroundPosition;
  if (!json.reveal.slideInnerBackgroundSize)
    json.reveal.slideInnerBackgroundSize =
      default_reveal.slideInnerBackgroundSize;
  if (!json.reveal.archContentAlignment)
    json.reveal.archContentAlignment = default_reveal.archContentAlignment;
  if (!json.reveal.capacityTags)
    json.reveal.capacityTags = default_reveal.capacityTags;
  if (!json.reveal.slideAutoAnimate)
    json.reveal.slideAutoAnimate = default_reveal.slideAutoAnimate;

  const SectionTitleArchImage = () => {
    let imageSrc = backgroundImageURIGenerator(
      json.reveal.slideInnerBackgroundImage,
      props.live,
      props.slide,
      props.presentation_id
    );

    return (
      <div className="image-container">
        <img
          className="image img-width-0 img-height-0"
          src={imageSrc}
          alt=""
          style={{
            filter: `saturate(${
              json?.reveal?.slideOverlayOpacity === 1 ? 1 : 0
            }) opacity(${
              json?.reveal?.slideOverlayOpacity
                ? json?.reveal?.slideOverlayOpacity
                : 0.7
            })`,
          }}
        />

        <div
          className="colour-filter"
          style={{ backgroundColor: json.reveal.slideInnerBackgroundColor }}
        ></div>

        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1366 768"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
        >
          <defs>
            <mask id="mask">
              <path
                fill="white"
                d="M1278.91,768H786c.29-.94,61.53-255.72,18.09-460.58C766.76,131.56,622.58.55,622,0h492c2.19,3.75,141.44,131.92,176.94,298.05,45,210.42-12.94,468.85-12.94,468.85C1278.05,767.23,1279,767.67,1278.91,768Z"
              />
            </mask>
          </defs>

          <image
            width="1366"
            height="768"
            href={imageSrc}
            mask={`${
              json.reveal.slideInnerBackgroundColor ? "url(#mask)" : ""
            }`}
          ></image>
        </svg>
      </div>
    );
  };

  const OneColumnArchImage = () => {
    return (
      <div className="overlay-container">
        <svg
          id="Layer_1"
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1920 1080"
        >
          <path
            fill="#fff"
            d="M1104.72,1079.73c-365.62,0-732.66-.09-1098.29,0-5.3,0-6.47-1.16-6.47-6.46Q.2,540,0,6.73C0,1.44,1.13.27,6.43.27c249,.15,618.79,0,867.78,0,.85.78,203.5,184.91,255.93,432.1C1191.2,720.3,1105.12,1078.41,1104.72,1079.73Z"
            transform="translate(0.04 -0.27)"
          />
          <path
            fill="#fff"
            d="M1565.74.27q174.3,0,348.51,0c4.81,0,5.76,1,5.75,5.76q-.21,534,0,1067.94c0,4.82-.93,5.8-5.75,5.76-38.88-.25-77.75,0-116.66,0,.11-.46-1.21-1.08-1.33-1.54,0,0,81.38-363.24,18.18-659C1764.55,185.69,1568.82,5.54,1565.74.27Z"
            transform="translate(0.04 -0.27)"
          />
        </svg>
      </div>
    );
  };

  const OneColumnHalfCircleImage = () => {
    switch (props.slide.theme.label) {
      case "rhg":
        return (
          <div className="overlay-container">
            <svg
              id="Layer_1"
              data-name="Layer 1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1366 768"
            >
              <rect fill="transparent" width="1366" height="768" />
              <path
                fill="#fff"
                d="M773.09,767.91q-384.24,0-768.5.09C.84,768,0,767.16,0,763.41Q.15,384,0,4.59C0,.84.84,0,4.59,0q384.26.15,768.5.09c-10,22.58-20.43,45-29.8,67.79-6.46,15.74-11.44,32.1-17.08,48.18l-5.67,17.84q-5.85,21.87-11.69,43.75-4.7,21.28-9.38,42.56L697.19,233l-.93,5-6.45,45.87-1.88,18.66c-.07.44-.14.88-.22,1.32l-.45,4.08q-.48,6-1,12.05-1,26-2.11,51.95c.57,23.63.61,47.28,1.83,70.88,2.15,41.78,8.23,83.09,17.16,124,3.69,16.85,8.34,33.48,12.55,50.21l5.49,18.85,1,4c.54,1.32,1.09,2.64,1.63,4l.12.06q1.51,5.08,3,10.17l1.23,5,1,2.87.91,2.88q1.08,2.12,2.17,4.23,1.15,4,2.32,8c.17.65.35,1.3.53,2,.53,1.37,1.05,2.75,1.58,4.12l.42,1.75,2.72,7.23,5.28,12.77c2.51,6.33,4.94,12.68,7.61,18.93.31.71,2,.85,3,1.26,0,1.3.07,2.6.11,3.91-.12.1-.35.25-.33.29a6.1,6.1,0,0,0,.41.7l3.17,7.06,2.1,5,1.87,4,2.06,4.89c1,2,2,4,3,6Z"
                transform="translate(0)"
              />
            </svg>
          </div>
        );
      case "companyapp":
      case "companyapp_2025":
      default:
        return (
          <div className="overlay-container">
            <svg
              id="Layer_1"
              data-name="Layer 1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 1920 1080"
            >
              <title>full-circle</title>
              <path
                fill="#fff"
                stroke="none"
                d="M0,0V1080H1920V0ZM1440,946c-224,0-405.5-181.55-405.5-405.5S1216.05,135,1440,135s405.5,181.55,405.5,405.5S1664,946,1440,946Z"
              />
            </svg>
          </div>
        );
    }
  };

  const ImageWithVerticalGradientOverlay = () => {
    return (
      <div
        className={`overlay-container alpha-${Math.round(
          json?.reveal?.slideOverlayOpacity * 10
        )} ${json?.reveal?.slideOverlayColor} overlay-${
          json?.reveal?.slideOverlayWidth
        } ${json?.reveal?.slideContentPosition}`}
      />
    );
  };

  useEffect(() => {
    let isImageBlock = false;
    let isSubtitleBlock = false;
    let contentIndex = 0;

    if (json.blocks && json.blocks.length > 0) {
      json.blocks.forEach((block) => {
        if (block.type === "image" || block.type === "inlineImage") {
          isImageBlock = true;
        }

        if (block.type === "subtitle") {
          isSubtitleBlock = true;
        }

        if (block.type === "body" || block.type === "list") {
          contentIndex++;
        }
      });
    } else {
      if (
        json.reveal.slideLogo === "" &&
        json.reveal.slideInnerBackgroundImage === "" &&
        json.reveal.slideBackgroundImage === ""
      ) {
        setSlideEmpty(true);
      }
    }

    setContentCount(contentIndex);
    setHasImage(isImageBlock);
    setHasSubtitle(isSubtitleBlock);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (
      json?.reveal?.slideLayout?.id === "image-gallery" ||
      json?.reveal?.slideLayout?.id === "fullscreen-image-gallery" ||
      json?.reveal?.slideLayout?.id === "artotel-image-gallery"
    ) {
      let newImages = [];
      json.blocks.forEach((block) => {
        if (block.type === "gallery") {
          block?.data?.images?.forEach((image) => {
            newImages.push(image);
          });
        }

        if (block.type === "title") {
          setTitle(block);
        }

        setImages(newImages);
      });
    }
    // eslint-disable-next-line
  }, []);

  // Click listener
  useEffect(() => {
    document
      .getElementById(`slide-${props.slide.id}`)
      ?.addEventListener("click", handleClicks, false);
    return () => {
      document
        .getElementById(`slide-${props.slide.id}`)
        ?.removeEventListener("click", handleClicks, false);
    };
    // eslint-disable-next-line
  }, []);

  const handleClicks = (event) => {
    const href = event?.target?.href;

    if (href && href !== "#") {
      event.stopPropagation();
      event.preventDefault();

      // gotoSlide
      if (
        href.indexOf("gotoSlide") > -1 ||
        href.indexOf("gotoslide") > -1 ||
        href.indexOf("goToSlide") > -1
      ) {
        return false;
      }

      // reloadSlide
      else if (
        href.indexOf("reloadSlide") > -1 ||
        href.indexOf("reloadslide") > -1
      ) {
        return false;
      }

      // reloads all slides
      else if (
        href.indexOf("reloadAllSlides") > -1 ||
        href.indexOf("reloadallslides") > -1
      ) {
        return false;
      }

      // openDocument
      else if (
        href.indexOf("opendocument") > -1 ||
        href.indexOf("openDocument") > -1
      ) {
        return false;
      }

      // openExternal
      else if (
        href.indexOf("openExternal") > -1 ||
        href.indexOf("openexternal") > -1
      ) {
        let link = href;
        link = link.split("link=");
        link = link[1];
        window.open(link);
        return false;
      }

      // open viewer nav bar
      // * from i.e. fullscreen iframes that prevent viewer defaults
      else if (
        href.indexOf("openViewerMenu") > -1 ||
        href.indexOf("openviewermenu") > -1
      ) {
        return false;
      } else {
        window.open(href);
      }
    }
  };

  const isArchOrCircle = () => {
    if (
      json.reveal.slideLayout.id === "one-column-arch-image" ||
      json.reveal.slideLayout.id === "one-column-half-circle-image" ||
      json.reveal.slideLayout.id === "campaign-info"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const testIframe = useCallback((src) => {
    fetch(src).then((res) => {
      if ([404, 403, 500].includes(res.status)) {
        setIframeError(true);
      }
    });
  }, []);

  const sidebarBtnClick = useCallback(() => {
    document.getElementById("content-wrapper").classList.toggle("active");
    const iframe = document.querySelector(`#map-${props.slide.id} iframe`);
    const iframeWindow = iframe?.contentWindow;

    iframeWindow?.document?.getElementById("map")?.classList.toggle("active");
    iframeWindow.offsetMap();
  }, [props.slide.id]);

  const oneColumnTextToggle = useCallback(() => {
    setToggleActive((prev) => !prev);
  }, []);

  const bgStyling = {
    backgroundSize: json.reveal.slideInnerBackgroundSize,
    backgroundRepeat: json.reveal.slideInnerBackgroundRepeat,
    backgroundPosition: json.reveal.slideInnerBackgroundPosition,
    opacity:
      json.reveal.slideLayout.id !== "one-column-arch-image" &&
      json.reveal.slideLayout.id !== "one-column-half-circle-image" &&
      json.reveal.slideLayout.id !== "campaign-info"
        ? json.reveal.slideInnerBackgroundOpacity
        : "",
    backgroundImage: isArchOrCircle()
      ? ""
      : json.reveal.slideInnerBackgroundImage !== ""
      ? `url("${backgroundImageURIGenerator(
          json.reveal.slideInnerBackgroundImage,
          props.live,
          props.slide,
          props.presentation_id
        )}")`
      : "",
  };

  const bgImage =
    json.reveal.slideBackgroundImage !== ""
      ? backgroundImageURIGenerator(
          json.reveal.slideBackgroundImage,
          props.live,
          props.slide,
          props.presentation_id
        )
      : "";

  let dataAutoAnimate = json.reveal.slideAutoAnimate
    ? {
        "data-auto-animate": "",
      }
    : {};

  const isImgOverlay =
    json?.reveal?.slideLayout.id === "image-with-vertical-overlay" ||
    json?.reveal?.slideLayout.id === "image-with-horizontal-overlay";

  const capacityString = () => {
    let classString = "";
    Object.keys(json.reveal.capacityTags).forEach((key) => {
      if (json.reveal.capacityTags[key]) {
        classString = `${classString} capacity-${key}`;
      }
    });
    return classString;
  };

  return (
    <>
      <section
        className={"slide-wrap h-100"}
        // reveal params
        data-transition={json.reveal.slideTransition}
        data-background-transition={json.reveal.slideBackgroundTransition}
        data-background-color={json.reveal.slideBackgroundColor}
        data-background-image={bgImage}
        data-background-opacity={json.reveal.slideBackgroundOpacity}
        data-background-repeat={json.reveal.slideBackgroundRepeat}
        data-background-position={json.reveal.slideBackgroundPosition}
        data-background-size={json.reveal.slideBackgroundSize}
        data-theme={props.slide.theme.label}
        data-slide-layout={json.reveal.slideLayout.id}
        {...dataAutoAnimate}
        onClick={(event) => {
          // Open links in new tab
          if (event.target.href) {
            event.preventDefault();
            window.open(event.target.href, "_blank");
          }
        }}
      >
        <div
          id={`slide-${props.slide.id}`}
          className={`slide ${isSafari && "safari"}`}
          style={
            json.reveal.slideLayout.id === "custom"
              ? { overflow: "hidden" }
              : {}
          }
        >
          <div
            className="slide-inner-background"
            style={{ backgroundColor: json.reveal.slideInnerBackgroundColor }}
          >
            <div className="slide-inner-background-content" style={bgStyling}>
              {json.reveal.slideLayout.id === "section-title-arch-image" && (
                <SectionTitleArchImage />
              )}

              {json.reveal.slideLayout.id === "one-column-arch-image" && (
                <OneColumnArchImage />
              )}

              {(json.reveal.slideLayout.id === "one-column-half-circle-image" ||
                json.reveal.slideLayout.id === "campaign-info") && (
                <OneColumnHalfCircleImage />
              )}

              {json.reveal.slideLayout.id ===
                "image-with-vertical-gradient-overlay" && (
                <ImageWithVerticalGradientOverlay />
              )}
              {isArchOrCircle() && (
                <div className="d-flex justify-content-end w-100 h-100">
                  <img
                    style={{
                      minWidth: 683,
                      maxWidth: "100%",
                      height: "100%",
                      objectFit: "cover",
                      marginRight:
                        json.reveal.slideLayout.id === "one-column-arch-image"
                          ? "3%"
                          : "",
                      opacity: json.reveal.slideInnerBackgroundOpacity,
                    }}
                    alt=""
                    data-src={backgroundImageURIGenerator(
                      json.reveal.slideInnerBackgroundImage,
                      props.live,
                      props.slide,
                      props.presentation_id
                    )}
                  />
                </div>
              )}
            </div>
          </div>
          <div
            className={`inner-container content-${contentCount} 
            ${
              json?.reveal?.slideLayout?.id === "one-column-text-toggle" &&
              toggleActive
                ? "active"
                : ""
            }
            ${hasImage ? "has-image" : ""} 
            ${hasSubtitle ? "has-subtitle" : ""} 
            ${
              json.reveal.slideOverlayColor ? json.reveal.slideOverlayColor : ""
            } 
            ${json.reveal.slideLayoutScheme} 
            ${json.reveal.slideContentPosition} 
            ${
              isArchOrCircle() && json?.reveal?.archContentAlignment
                ? "top"
                : ""
            } 
            ${
              json?.reveal?.slideLayout.id === "data-table"
                ? `stub-column-${
                    json?.reveal?.slideDataTableColumn
                      ? json?.reveal?.slideDataTableColumn
                      : 0
                  }`
                : ""
            }
            ${isImgOverlay ? "img-overlay" : ""}
            ${
              isImgOverlay
                ? `alpha-${Math.round(json?.reveal?.slideOverlayOpacity * 10)}`
                : ""
            }
            ${
              json?.reveal?.slideLayout.id ===
              "image-with-vertical-gradient-overlay"
                ? `content-${json?.reveal?.slideContentWidth}`
                : ""
            }
            ${
              json?.reveal?.slideLayout.id === "at-a-glance" && capacityString()
            }
            ${
              props.slide.theme.label === "artotel" &&
              json?.reveal?.slidePattern !== ""
                ? json?.reveal?.slidePattern
                : ""
            }
            `}
            style={
              json?.reveal?.slideLayout.id === "image-with-vertical-overlay"
                ? {
                    // backgroundColor: `rgba(${overlayRGB}, ${json?.reveal?.slideOverlayOpacity})`,
                    // float: json?.reveal?.slideContentPosition,
                    width: `${json?.reveal?.slideOverlayWidth}%`,
                  }
                : json?.reveal?.slideLayout.id ===
                  "image-with-horizontal-overlay"
                ? {
                    // backgroundColor: `rgba(${overlayRGB}, ${json?.reveal?.slideOverlayOpacity})`,
                    position: "absolute",
                    height:
                      json?.reveal?.slideOverlayHeight >= 20
                        ? `${json?.reveal?.slideOverlayHeight}%`
                        : `auto`,
                    // top: json?.reveal?.slideContentPosition === 'left' ? 0 : 'auto',
                    // bottom: json?.reveal?.slideContentPosition === 'left' ? 'auto' : 0,
                  }
                : {}
            }
          >
            {json?.reveal?.slideLayout?.id === "advanced" && (
              <>
                {iframeError ? (
                  <div
                    className="content-wrapper w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ backgroundColor: "#fff" }}
                  >
                    <h6 style={{ color: "#888" }}>
                      Error loading slide. The slide may be missing or corrupt.
                    </h6>
                  </div>
                ) : (
                  <Iframe
                    id={props.slide.id}
                    slide={props.slide}
                    live={props.live}
                    presentation_id={props.presentation_id}
                    onTest={testIframe}
                  />
                )}
              </>
            )}

            {json?.reveal?.slideLayout?.id === "artotel-image-gallery" &&
              props.slide.theme.label === "artotel" && (
                <div className="content-wrapper">
                  <BespokeImageGallery
                    presentation_id={props.presentation_id}
                    live={props.live}
                    title={title}
                    images={images}
                  />
                </div>
              )}

            {json?.reveal?.slideLayout?.id === "image-gallery" && (
              <div className="content-wrapper">
                <ImageGallery
                  presentation_id={props.presentation_id}
                  live={props.live}
                  title={title}
                  images={images}
                  variant={
                    props.slide.theme.label === "artotel" ? "artotel" : ""
                  }
                />
              </div>
            )}

            {json?.reveal?.slideLayout?.id === "fullscreen-image-gallery" && (
              <div className="content-wrapper">
                <FullscreenImageGallery
                  presentation_id={props.presentation_id}
                  live={props.live}
                  images={images}
                />
              </div>
            )}

            {json?.reveal?.slideLayout?.id === "location-map" && (
              <>
                <div
                  className="map-wrapper"
                  dangerouslySetInnerHTML={{
                    __html: editorMapParser.parse(json).join(""),
                  }}
                />
                <div id="content-wrapper" className={`sidebar content-wrapper`}>
                  <div
                    className="content"
                    dangerouslySetInnerHTML={{
                      __html: editorParser.parse(json).join(""),
                    }}
                  />
                  <IconButton className="sidebar-btn" onClick={sidebarBtnClick}>
                    <ChevronLeftIcon className="left" />
                    <ChevronRightIcon className="right" />
                  </IconButton>
                </div>
              </>
            )}
            {json?.reveal?.slideLayout?.id === "country-map" && (
              <>
                <div className="map-wrapper">
                  <div className="map">
                    <div className="map-container">
                      <CountryMapIframe
                        theme_json={theme_json}
                        themeDirectory={themeDirectory}
                        presentation_id={props.presentation_id}
                        live={props.live}
                        json={json}
                        slide={{ id: props.slide.id }}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {json?.reveal?.slideLayout?.id === "custom" && (
              <CustomLayoutCountryMap
                theme_json={theme_json}
                themeDirectory={themeDirectory}
                presentation_id={props.presentation_id}
                live={props.live}
                json={json}
                slide={{ id: props.slide.id }}
              />
            )}
            {json?.reveal?.slideLayout?.id === "one-column-text-toggle" && (
              <button
                className="toggle-btn"
                dangerouslySetInnerHTML={{ __html: AccordionArrow }}
                onClick={oneColumnTextToggle}
              ></button>
            )}
            {json?.reveal?.slideLayout?.id !== "advanced" &&
              json?.reveal?.slideLayout?.id !== "image-gallery" &&
              json?.reveal?.slideLayout?.id !== "artotel-image-gallery" &&
              json?.reveal?.slideLayout?.id !== "fullscreen-image-gallery" &&
              json?.reveal?.slideLayout?.id !== "location-map" &&
              json?.reveal?.slideLayout?.id !== "country-map" && (
                <>
                  {isSlideEmpty ? (
                    <div
                      className="content-wrapper w-100 h-100 d-flex justify-content-center align-items-center"
                      style={{ backgroundColor: "#fff" }}
                    >
                      <h6 style={{ color: "#888" }}>
                        This slide has no content.
                      </h6>
                    </div>
                  ) : (
                    <div
                      className="content-wrapper"
                      dangerouslySetInnerHTML={{
                        __html: editorParser.parse(json).join(""),
                      }}
                    />
                  )}
                </>
              )}
          </div>
          {json.reveal.slideLogo !== "" && (
            <div className={`logo-container ${json.reveal.slideLogoPosition}`}>
              {props.live ? (
                <img
                  data-src={`${themeDirectory}/dist/images/logos/${json.reveal.slideLogo}`}
                  alt={`slide-logo`}
                />
              ) : (
                <img
                  data-src={`${themeDirectory}/dist/images/logos/${json.reveal.slideLogo}`}
                  alt={`slide-logo`}
                />
              )}
            </div>
          )}
          {json.reveal.slideLockup && json.reveal.slideLockup !== "" && (
            <div
              className={`lockup-container ${json.reveal.slideLockupPosition}`}
            >
              {props.live ? (
                <img
                  data-src={`${themeDirectory}/dist/images/lockups/${json.reveal.slideLockup}`}
                  alt={`slide-lockup`}
                />
              ) : (
                <img
                  data-src={`${themeDirectory}/dist/images/lockups/${json.reveal.slideLockup}`}
                  alt={`slide-lockup`}
                />
              )}
            </div>
          )}
        </div>
        <aside
          className="notes"
          dangerouslySetInnerHTML={{
            __html: props.slideNotes?.trim().replace(/\n/g, "<br/>") ?? "",
          }}
        ></aside>
      </section>
    </>
  );
}

export default React.memo(Slide, (prevProps, nextProps) => {
  return (
    prevProps.slide.id === nextProps.slide.id &&
    prevProps.slide.slide_json === nextProps.slide.slide_json &&
    prevProps.slide.theme.label === nextProps.slide.theme.label &&
    prevProps.slideNotes === nextProps.slideNotes &&
    prevProps.live === nextProps.live &&
    prevProps.presentation_id === nextProps.presentation_id
  );
});
